import { Component, ChangeDetectionStrategy, inject, signal, computed, OnInit, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { VerifyStorageAvailableResponse, PipesModule } from '@xmedical/core';
import { FileUploadItemComponent, MAX_UPLOAD_SIZE_GB } from '../file-upload-item/file-upload-item.component';
import { FileUploaderService } from '../../services/file-uploader.service';
import { FileUploadEventsService, UploadProgressEvent } from '../../services/file-upload-events.service';
import { UploadedFile } from '../../models/uploaded-file.model';
import { UploadType } from '../../models/upload-type.type';

export interface FileUploaderDialogData {
  files: FileList;
  tags: string[];
  type: UploadType;
  patientId: number | null;
  commentId: number | null;
}

@Component({
  selector: 'xmed-file-uploader',
  standalone: true,
  imports: [
    MatProgressBarModule,
    MatButtonModule,
    PipesModule,
    FileUploadItemComponent
  ],
  templateUrl: './file-uploader.component.html',
  styleUrl: './file-uploader.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileUploaderComponent implements OnInit, OnDestroy {
  private readonly bottomSheetData = inject<FileUploaderDialogData>(MAT_BOTTOM_SHEET_DATA, { optional: true });
  private readonly dialogData = inject<FileUploaderDialogData>(MAT_DIALOG_DATA, { optional: true });
  private readonly bottomSheetRef = inject(MatBottomSheetRef<FileUploaderComponent>, { optional: true });
  private readonly dialogRef = inject(MatDialogRef<FileUploaderComponent>, { optional: true });
  private readonly uploadEvents = inject(FileUploadEventsService);
  private readonly uploaderService = inject(FileUploaderService);

  @ViewChildren(FileUploadItemComponent) uploads!: QueryList<FileUploadItemComponent>;

  // State signals
  verifying = signal(false);
  processing = signal(false);
  fileUploadCount = signal(0);
  fileErrorCount = signal(0);
  fileUploadProgress = signal(0);
  totalSize = signal(0);
  files = signal<File[]>([]);
  filesProgress = signal<UploadedFile[]>([]);
  storageAvailabilityCheckResponse = signal<VerifyStorageAvailableResponse | null>(null);

  // Data from bottomsheet or dialog
  tags = signal<string[]>([]);
  uploadType = signal<UploadType>('practice');
  patientId = signal<number | null>(null);
  commentId = signal<number | null>(null);

  private uploadEventsSubscription: Subscription | null = null;

  // Computed values
  totalFiles = computed(() => this.files().length);

  fileSizeProgress = computed(() => {
    const size = this.totalSize();
    const progress = this.fileUploadProgress();
    return (size / 100) * progress;
  });

  uploadAttemptComplete = computed(() => {
    return this.files().length === (this.fileUploadCount() + this.fileErrorCount());
  });

  uploadAttemptSuccessful = computed(() => {
    return this.files().length === this.fileUploadCount();
  });

  storageAvailable = computed(() => {
    const response = this.storageAvailabilityCheckResponse();
    if (!response) {
      return true;
    }
    return response.storageAvailableFlag;
  });

  /** True when any selected file has no extension. Such a file is flagged and cannot be
   *  uploaded, so the Upload button is disabled until it is removed. Mirrors the per-item
   *  extension check in FileUploadItemComponent. */
  hasExtensionlessFile = computed(() => this.files().some(file => !this.hasExtension(file.name)));

  /** True when any selected file exceeds the per-file size limit. Mirrors the per-item size
   *  check in FileUploadItemComponent; such a file is flagged and blocks the upload. */
  hasOversizedFile = computed(() => this.files().some(file => file.size > MAX_UPLOAD_SIZE_GB));

  /** Any selected file that is flagged (no extension or too large) blocks the whole batch. */
  hasBlockedFile = computed(() => this.hasExtensionlessFile() || this.hasOversizedFile());

  availableStorageSize = computed(() => {
    return this.storageAvailabilityCheckResponse()?.storageAvailable ?? 0;
  });

  totalStorageUsedSize = computed(() => {
    return this.storageAvailabilityCheckResponse()?.storageUsed ?? 0;
  });

  totalStorageTotalSize = computed(() => {
    return this.storageAvailabilityCheckResponse()?.storageTotal ?? 0;
  });

  constructor() {
    const data = this.bottomSheetData ?? this.dialogData;
    if (data) {
      if (data.files) {
        this.filesLoaded(data.files);
      }
      if (data.patientId !== null && data.patientId !== undefined) {
        this.patientId.set(data.patientId);
      }
      if (data.tags) {
        this.tags.set(data.tags);
      }
      if (data.commentId !== null && data.commentId !== undefined) {
        this.commentId.set(data.commentId);
      }
      if (data.type) {
        this.uploadType.set(data.type);
      }
    }
  }

  ngOnInit(): void {
    this.uploadEventsSubscription = this.uploadEvents.uploadEvents$
      .subscribe(event => this.uploadEventHandler(event));
  }

  ngOnDestroy(): void {
    this.uploadEventsSubscription?.unsubscribe();
  }

  filesLoaded(fileList: FileList): void {
    const currentFiles = [...this.files()];
    const currentProgress = [...this.filesProgress()];

    for (let index = 0; index < fileList.length; index++) {
      const file = fileList[index] as File;
      currentFiles.push(file);
      currentProgress.push(new UploadedFile(file.name, file.size));
    }

    this.files.set(currentFiles);
    this.filesProgress.set(currentProgress);
    this.verifyStorageAvailable();
  }

  deleteFile(index: number): void {
    const currentFiles = [...this.files()];
    currentFiles.splice(index, 1);
    this.files.set(currentFiles);

    if (currentFiles.length === 0) {
      this.close(false);
    }
    this.verifyStorageAvailable();
  }

  clearFiles(success = false): void {
    this.files.set([]);
    this.fileUploadProgress.set(0);
    this.fileUploadCount.set(0);
    this.totalSize.set(this.calculateUploadSize());
    this.close(success);
  }

  uploadFiles(): void {
    this.processing.set(true);
    this.uploads.forEach(upload => {
      upload.startUpload();
    });
  }

  close(success = false): void {
    if (this.bottomSheetRef) {
      this.bottomSheetRef.dismiss(success);
    } else if (this.dialogRef) {
      this.dialogRef.close(success);
    }
  }

  private uploadEventHandler(event: UploadProgressEvent): void {
    let progress = 0;
    const currentProgress = [...this.filesProgress()];
    const numberOfFiles = currentProgress.length;

    for (let i = 0; i < numberOfFiles; i++) {
      const file = currentProgress[i];
      if (file.name === event.fileName) {
        file.progress = event.progress;
        currentProgress[i] = file;
      }
      progress += file.progress / numberOfFiles;
    }

    this.filesProgress.set(currentProgress);

    if (event.type === 'complete') {
      this.fileUploadCount.update(count => count + 1);
    }
    if (event.type === 'error') {
      this.fileErrorCount.update(count => count + 1);
    }

    this.fileUploadProgress.set(progress);

    if (this.uploadAttemptComplete()) {
      this.processing.set(false);
    }
    if (this.uploadAttemptSuccessful()) {
      this.clearFiles(true);
    }
  }

  private verifyStorageAvailable(): void {
    const size = this.calculateUploadSize();
    this.totalSize.set(size);
    this.verifying.set(true);

    this.uploaderService.verifyStorageAvailable(size).subscribe({
      next: (result) => {
        this.storageAvailabilityCheckResponse.set(result);
        this.verifying.set(false);
      },
      error: (error) => {
        console.error(error);
        this.verifying.set(false);
      }
    });
  }

  private calculateUploadSize(): number {
    return this.files().reduce((total, file) => total + file.size, 0);
  }

  /** A name has a real extension when there is a dot that is neither the first character nor the
   *  last (so ".gitignore" and "report." count as having none). */
  private hasExtension(name: string): boolean {
    const lastDot = name.lastIndexOf('.');
    return lastDot > 0 && lastDot < name.length - 1;
  }
}
